import { css, html, LitElement, PropertyValues } from "lit";
import { repeat } from "lit/directives/repeat.js";
import { property } from "lit/decorators/property.js";
import { classMap } from "lit/directives/class-map.js";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import { state } from "lit/decorators/state.js";
import debounce from "lodash-es/debounce";
import { AsyncController } from "../../shared/controllers/async-controller";
import { DEFAULT_BASE_URL, SUPPORTED_LANGUAGES, WIDGET_NAMES } from "../../api-sdk/constants";
import "../../shared/components/progress-bar";
import "../../shared/components/promo-pill-label";
import "../beam-partner-logos";
import { defineCustomElement } from "../../shared/defineCustomElement";
import { _loading } from "../../shared/fragments/loading-template";
import { _errorMessage } from "../../shared/fragments/error-message-template";
import { defineCustomText, useCustomText } from "../../shared/fragments/custom-text";
import { cssReset } from "../../shared/css-reset";
import { _cssVariableMap } from "../../shared/fragments/css-variable-map";
import { enforceConfig, areRequiredKeysDefined } from "../../shared/enforce-config";
import { BeamError, MissingConfig } from "../../utils/beam-errors";
import { TId, TNumericId } from "../../shared/types";
import { TCart } from "../../shared/cart-contents";
import { createScopedLocalStorage } from "../../utils/local-storage";
import { cssCardGrid } from "../../shared/fragments/css-card-grid";
import { postChainEligibleNonprofitsForCart, putTransaction } from "../../api-sdk/v3/routes";
import { LANGUAGES } from "../../api-sdk/types";
import { BeamNonprofitSelectEvent } from "../../utils/events";
import { localizeUserString } from "../../shared/localize";
import { progressBarConfigDefaults } from "../../shared/components/progress-bar";
import { partnerLogosConfigDefaults } from "../beam-partner-logos";
import { promoPillLabelConfigDefaults } from "../../shared/components/promo-pill-label";
import { _radioButton } from "../../shared/fragments/radioButton";
import { strings } from "./strings";

interface TRequiredConfig {
  apiKey: string;
  transactionId: TId;
}

type TIAPIV3GetChainNonprofits = Awaited<ReturnType<typeof import("../../api-sdk/v3/routes").getChainNonprofits>>;
type TIAPIV3EligibleNonprofits = Awaited<
  ReturnType<typeof import("../../api-sdk/v3/routes").getChainNonprofits>
>["nonprofits"];
type TIAPIV3Nonprofit = TIAPIV3EligibleNonprofits[number];
export class BeamRedeemTransaction extends LitElement {
  static tagName = "beam-redeem-transaction";

  @property({ type: String }) public baseUrl: string = DEFAULT_BASE_URL;

  @property({ type: String }) public apiKey?: TRequiredConfig["apiKey"];

  @property({ type: Number }) public transactionId?: TRequiredConfig["transactionId"];

  @property({ type: Number }) public storeId?: TNumericId;

  @property({ type: String }) public countryCode?: string;

  @property({ type: String }) public postalCode?: string;

  @property({ type: Object }) public cart?: TCart;

  @property({ type: Number, reflect: true })
  public selectedNonprofitId?: TNumericId;

  @property({ type: String }) public lang: LANGUAGES = "en";

  @property({ type: Boolean }) public debug = false;

  @property({ type: Boolean }) public draftConfig = false;

  @state() private isMobile = window.innerWidth < 768;

  @state() private didUserExpandNonprofits = false;

  @state() private shouldUsePromoUI = false;

  get configLang() {
    return SUPPORTED_LANGUAGES[this.lang] || "en";
  }

  private get hasCorrectTransactionConfig(): boolean {
    const requiredKeys: (keyof TRequiredConfig)[] = ["apiKey", "transactionId"];
    const isTransactionConfigCorrect = areRequiredKeysDefined(requiredKeys, this);
    return isTransactionConfigCorrect;
  }

  private getChainNonprofits = async () => {
    if (!this.hasCorrectTransactionConfig) {
      throw new MissingConfig();
    }

    const res = await postChainEligibleNonprofitsForCart({
      baseUrl: this.baseUrl,
      headers: { authorization: `Api-Key ${this.apiKey}` },
      requestBody: {
        storeId: this.storeId,
        postalCode: this.postalCode,
        countryCode: this.countryCode,
        transactionId: String(this.transactionId),
        cart: this.cart ?? undefined,
        widgetName: WIDGET_NAMES.redeem_transaction,
        options: {
          config: {
            draftConfig: this.draftConfig,
          },
        },
        version: "2.0.0",
        lang: this.configLang,
      },
    });

    // If store ID was not provided, get the store ID from response here
    if (res.store?.id && res.store.id !== this.storeId) {
      this.storeId = res.store.id;
    }

    this.localStorage.setItemJson("chainNonprofits", {
      createdAt: new Date(),
      data: res,
    });

    return res;
  };

  private updateNonprofitIdForRecord = async ({ selectedNonprofitId }: { selectedNonprofitId: TNumericId }) => {
    if (this.transactionId) {
      return this.putTransaction({ selectedNonprofitId });
    }
    return null;
  };
  private putTransaction = async ({ selectedNonprofitId }: { selectedNonprofitId: TNumericId }) => {
    if (!enforceConfig<TRequiredConfig>(["apiKey", "transactionId"], this)) {
      throw new MissingConfig();
    }
    return putTransaction({
      baseUrl: this.baseUrl,
      headers: {
        authorization: `Api-Key ${this.apiKey}`,
      },
      pathParams: {
        transactionId: Number(this.transactionId), // TODO SDK-2388: fix api spec for PUT transaction/:id to use string
      },
      requestBody: {
        nonprofitId: selectedNonprofitId,
      },
    });
  };

  private nonprofitListDataController = new AsyncController<typeof this.getChainNonprofits>(
    this,
    this.getChainNonprofits
  );

  private selectionDataController = new AsyncController<typeof this.updateNonprofitIdForRecord>(
    this,
    this.updateNonprofitIdForRecord
  );

  private localStorage = createScopedLocalStorage(this as LitElement & TRequiredConfig);

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener("resize", this.evaluateBreakPoints);
  }

  async firstUpdated(_changedProperties: PropertyValues) {
    await this.restoreStateFromCache();
  }

  async updated(previousPropertyValues: PropertyValues) {
    // Reload nonprofit list on change of any of these props:
    // Also fires on first load as props go from undefined => value
    const requireNewDataProps = ["baseUrl", "storeId", "apiKey", "countryCode", "postalCode", "lang"];
    for (const prop of requireNewDataProps) {
      if (previousPropertyValues.has(prop)) {
        await this.nonprofitListDataController.exec();
        break;
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  private restoreStateFromCache() {
    if (!enforceConfig<TRequiredConfig>(["apiKey"], this)) throw new MissingConfig();

    this.selectedNonprofitId = parseInt(this.localStorage.getItem("nonprofit") || "") || undefined;
    try {
      // try to restore nonprofit list if we have data and it's not too old
      // list will continue to refresh async and replace this data when ready
      const { createdAt = 0, data } =
        this.localStorage.getItemJson<{ createdAt: string; data: any }>("chainNonprofits") || {};
      const cacheTtl = 2 * 60 * 60 * 1000;
      if (new Date(createdAt).valueOf() + cacheTtl > new Date().valueOf()) {
        this.nonprofitListDataController.data = data;
        this.nonprofitListDataController.loading = false;
      }
    } catch (err) {
      // ignore cache retrieval error and continue to fetch data
    }
  }

  /**
   * Factory for selection event handler
   *
   * Nonprofit selector implements radio-button semantics:
   * * If nothing is selected, tabbing into selector selects first card
   * * Arrow keys changes focus between cards, but doesn't select
   * * Enter/Space sets selection
   * * If a nonprofit is selected, arrow keys change focus AND selection
   * * Click sets selection
   * @param {number} id
   * @param {number} index
   * @param {{id: number}[]} nonprofits
   * @returns {(evt: Event) => void}
   */
  private makeHandleSelect =
    (id: number, index: number, nonprofits: { nonprofit: { id: number } }[]): ((event: Event) => void) =>
    async (event: Event) => {
      if (!this.allowChange) return;
      const currentId = this.selectedNonprofitId;
      if (event instanceof KeyboardEvent) {
        let nextFocus = null;
        switch (event.key) {
          case "ArrowUp":
          case "ArrowLeft":
            if (index === 0) {
              nextFocus = nonprofits[nonprofits.length - 1];
            } else {
              nextFocus = nonprofits[index - 1];
            }
            event.preventDefault();
            break;
          case "ArrowRight":
          case "ArrowDown":
            if (index === nonprofits.length - 1) {
              nextFocus = nonprofits[0];
            } else {
              nextFocus = nonprofits[index + 1];
            }
            event.preventDefault();
            break;
          case "Enter":
          case " ":
            event.preventDefault();
            break; // continue to toggle-selection block below
          default:
            return;
        }
        if (nextFocus) {
          const focusTarget = this.renderRoot.querySelector(`[data-value="${nextFocus.nonprofit.id}"]`) as HTMLElement;
          if (focusTarget !== null) {
            focusTarget.tabIndex = 0;
            focusTarget.focus();
          }
          return;
        }
      }
      // Handle selection with click or Enter/Space key
      const targetEl = event.currentTarget;
      if (targetEl instanceof HTMLElement) {
        if (currentId === id) {
          // this["selected-nonprofit-id"] = undefined; // unset (not supported)
          return; // no API call or localStorage change needed
        } else {
          this.selectedNonprofitId = id;
        }
      }
    };

  get allowChange() {
    // Disallow change if already submitted
    return this.selectionDataController.data == null && !this.selectionDataController.loading;
  }

  get allowSubmit() {
    // Disabled if no nonprofit has been selected yet or if submission was already received
    return Boolean(this.selectedNonprofitId && this.allowChange);
  }

  handleSubmit = async () => {
    const { selectedNonprofitId } = this;
    if (!this.allowSubmit || selectedNonprofitId == null) return;
    await this.selectionDataController.exec({ selectedNonprofitId });

    this.localStorage.setItem("nonprofit", selectedNonprofitId);
    this.localStorage.setItem("nonprofit_selected_at", new Date().toISOString());

    const selectedNonprofit = this.nonprofitListDataController?.data?.nonprofits.find(
      (np) => np.nonprofit.id === selectedNonprofitId
    );

    this.dispatchEvent(
      new BeamNonprofitSelectEvent({
        selectedNonprofitId,
        nonprofitName: selectedNonprofit?.nonprofit.name,
        source: WIDGET_NAMES.redeem_transaction,
      })
    );
  };

  evaluateBreakPoints = debounce(
    () => {
      this.isMobile = window.innerWidth < 768;
    },
    50,
    { maxWait: 50, leading: true }
  );

  public get cssVariables() {
    const defaults = {
      "--beam-fontFamily": "inherit",
      "--beam-fontStyle": "inherit",
      "--beam-fontSize": "inherit",
      "--beam-textColor": "inherit",
      "--beam-backgroundColor": "inherit",
      ...defineCustomText("--beam-RedeemTransaction-title", {
        fontSize: "1.25em",
        fontWeight: "bold",
      }),
      ...defineCustomText("--beam-RedeemTransaction-description", {
        marginTop: "0.5em",
        lineHeight: "normal",
      }),
      ...partnerLogosConfigDefaults,
      ...progressBarConfigDefaults,
      "--beam-RedeemTransaction-maxWidth": "1000px",
      "--beam-RedeemTransaction-submitButton-fontSize": "14px",
      "--beam-RedeemTransaction-submitButton-fontWeight": "inherit",
      "--beam-RedeemTransaction-submitButton-fontFamily": "inherit",
      "--beam-RedeemTransaction-submitButton-textTransform": "uppercase",
      "--beam-RedeemTransaction-submitButton-color": "#fff",
      "--beam-RedeemTransaction-submitButton-backgroundColor": "#000000",
      "--beam-RedeemTransaction-submitButton-borderRadius": "0px",
      "--beam-RedeemTransaction-submitButton-borderColor": "#000000",
      "--beam-RedeemTransaction-submitButton-padding": "16px 0px",
      "--beam-RedeemTransaction-submitButton-width": "100%",
      "--beam-RedeemTransaction-submitButton-marginTop": "10px",
      "--beam-RedeemTransaction-submitButton-marginBottom": "10px",
      "--beam-RedeemTransaction-submitButtonContainer-display": "flex",
      "--beam-RedeemTransaction-submitButtonContainer-backgroundColor": "#fff",
      "--beam-RedeemTransaction-submitButton-hover-backgroundColor": "#000000",
      "--beam-RedeemTransaction-submitButton-hover-borderColor": "#000000",
      "--beam-RedeemTransaction-submitButton-hover-color": "#fff",
      "--beam-RedeemTransaction-submitButton-disabled-borderColor": "#767676",
      "--beam-RedeemTransaction-submitButton-disabled-backgroundColor": "#767676",
      "--beam-RedeemTransaction-submitButton-disabled-color": "#fff",
      "--beam-RedeemTransaction-radioButton-color": "transparent",
      "--beam-RedeemTransaction-radioButton-borderColor": "#707070",
      "--beam-RedeemTransaction-radioButton-backgroundColor": "#fff",
      "--beam-RedeemTransaction-radioButton-hover-color": "#fff",
      "--beam-RedeemTransaction-radioButton-hover-backgroundColor": "#000",
      "--beam-RedeemTransaction-radioButton-hover-borderColor": "#000",
      "--beam-RedeemTransaction-radioButton-selected-borderColor": "#000",
      "--beam-RedeemTransaction-radioButton-selected-backgroundColor": "#000",
      "--beam-RedeemTransaction-radioButton-selected-color": "#fff",
      "--beam-RedeemTransaction-options-minWidth": "0px",
      "--beam-RedeemTransaction-options-columnCount": "1",
      "--beam-RedeemTransaction-options-borderWidth": "1px",
      "--beam-RedeemTransaction-options-image-borderRadius": "8px",
      "--beam-RedeemTransaction-options-image-height": "70px",
      "--beam-RedeemTransaction-options-image-height-mobile": "75px",
      "--beam-RedeemTransaction-options-borderColor": "#d9d9d9",
      "--beam-RedeemTransaction-options-backgroundColor": "transparent",
      "--beam-RedeemTransaction-options-marginTop": "15px",
      "--beam-RedeemTransaction-options-hover-backgroundColor": "#f1f1f1",
      "--beam-RedeemTransaction-options-hover-borderWidth": ".5px",
      "--beam-RedeemTransaction-options-hover-borderColor": "#000000",
      "--beam-RedeemTransaction-options-selected-borderColor": "#000000",
      "--beam-RedeemTransaction-options-selected-borderWidth": ".5px",
      "--beam-RedeemTransaction-options-selected-backgroundColor": "#eee",
      ...defineCustomText("--beam-RedeemTransaction-options-details", {
        fontSize: "0.85em",
        marginTop: "0px",
        lineHeight: "normal",
        letterSpacing: "-0.45px",
      }),
      ...defineCustomText("--beam-RedeemTransaction-options-details-cause", {
        fontSize: "1em",
        fontWeight: "bold",
      }),
      "--beam-RedeemTransaction-options-details-nonprofitName-fontWeight": "normal",
      "--beam-RedeemTransaction-options-details-nonprofitName-fontStyle": "inherit",
      "--beam-RedeemTransaction-options-details-fundingProgress-marginTop": "3px", // update after class name addition
      ...defineCustomText("--beam-RedeemTransaction-options-details-fundingProgressLabel", {
        fontSize: "0.85em",
        textTransform: "capitalize",
      }),
      "--beam-RedeemTransaction-cardStyle": "'image'", // icon, selected_icon, image
      "--beam-RedeemTransaction-options-icon-padding": "0px",
      "--beam-RedeemTransaction-options-icon-backgroundColor": "#F3F3F3",
      "--beam-RedeemTransaction-options-collapseNonprofits-mobile": "false",
      "--beam-RedeemTransaction-options-collapseNonprofits-desktop": "false",
      "--beam-RedeemTransaction-image-width": "13%",
      "--beam-RedeemTransaction-image-height": "50%",
      "--beam-RedeemTransaction-image-width-mobile": "16%",
      ...promoPillLabelConfigDefaults,
    };

    const remoteConfig = this.nonprofitListDataController?.data?.config?.web?.theme || {};

    const config = { ...defaults, ...remoteConfig };

    const serializable = Object.create({
      toCSS() {
        return _cssVariableMap(this as Record<string, string>);
      },
    });

    return Object.assign(serializable, config);
  }

  static styles = [
    cssReset,
    css`
      :host {
        display: block;
        max-width: var(--beam-RedeemTransaction-maxWidth);
        font-family: var(--beam-fontFamily);
        font-style: var(--beam-fontStyle);
        font-size: var(--beam-fontSize);
        background-color: var(--beam-backgroundColor);
        color: var(--beam-textColor);
        word-break: normal;
      }

      .headerContainer {
        align-items: flex-start;
        justify-content: space-between;
        display: flex;
        order: 2;
      }

      .submitButtonContainer {
        margin-top: var(--beam-RedeemTransaction-submitButton-marginTop);
        margin-bottom: var(--beam-RedeemTransaction-submitButton-marginBottom);
        background-color: var(--beam-RedeemTransaction-submitButtonContainer-backgroundColor);
        display: var(--beam-RedeemTransaction-submitButtonContainer-display);
      }

      .submitButton {
        color: var(--beam-RedeemTransaction-submitButton-color);
        background-color: var(--beam-RedeemTransaction-submitButton-backgroundColor);
        border: 1px solid var(--beam-RedeemTransaction-submitButton-borderColor);
        font-size: var(--beam-RedeemTransaction-submitButton-fontSize);
        font-weight: var(--beam-RedeemTransaction-submitButton-fontWeight);
        font-family: var(--beam-RedeemTransaction-submitButton-fontFamily);
        text-transform: var(--beam-RedeemTransaction-submitButton-textTransform);
        border-radius: var(--beam-RedeemTransaction-submitButton-borderRadius);
        padding: var(--beam-RedeemTransaction-submitButton-padding);
        width: var(--beam-RedeemTransaction-submitButton-width);
        transition: background-color 0.2s, border-color 0.2s, color 0.2s;
        cursor: pointer;
      }

      .submitButton:not(.isDisabled):focus-visible,
      .submitButton:not(.isDisabled):hover {
        background-color: var(--beam-RedeemTransaction-submitButton-hover-backgroundColor);
        border-color: var(--beam-RedeemTransaction-submitButton-hover-borderColor);
        color: var(--beam-RedeemTransaction-submitButton-hover-color);
      }

      .submitButton.isDisabled {
        color: var(--beam-RedeemTransaction-submitButton-disabled-color);
        background-color: var(--beam-RedeemTransaction-submitButton-disabled-backgroundColor);
        border-color: var(--beam-RedeemTransaction-submitButton-disabled-borderColor);
        cursor: not-allowed;
      }

      .options {
        margin-top: var(--beam-RedeemTransaction-options-marginTop);
        order: 3;
        flex-grow: 1;
      }

      .options.isHidden {
        display: none;
      }

      .option {
        border: var(--beam-RedeemTransaction-options-borderWidth) solid
          var(--beam-RedeemTransaction-options-borderColor);
        border-bottom: none;
        background-color: var(--beam-RedeemTransaction-options-backgroundColor);
        cursor: pointer;
        transition: border-color 0.2s, background-color 0.2s, outline 0.2s;
      }

      .option:first-child {
        border-radius: 8px 8px 0 0; /* Top corners rounded */
      }

      .option:last-child {
        border-bottom: var(--beam-RedeemTransaction-options-borderWidth) solid
          var(--beam-RedeemTransaction-options-borderColor);
        border-radius: 0 0 8px 8px; /* Bottom corners rounded */
      }

      .option:not(.isDisabled):not(:last-child):focus-visible,
      .option:not(.isDisabled):not(:last-child):hover,
      .option:not(.isSelected):not(:last-child):focus-visible,
      .option:not(.isSelected):not(:last-child):hover {
        background-color: var(--beam-RedeemTransaction-options-hover-backgroundColor);
        outline: var(--beam-RedeemTransaction-options-hover-borderWidth) solid
          var(--beam-RedeemTransaction-options-hover-borderColor);
        border-bottom: var(--beam-RedeemTransaction-options-hover-borderWidth) solid
          var(--beam-RedeemTransaction-options-hover-borderColor);
      }

      .option:not(.isDisabled):focus-visible,
      .option:not(.isDisabled):hover,
      .option:not(.isSelected):focus-visible,
      .option:not(.isSelected):hover {
        background-color: var(--beam-RedeemTransaction-options-hover-backgroundColor);
        outline: var(--beam-RedeemTransaction-options-hover-borderWidth) solid
          var(--beam-RedeemTransaction-options-hover-borderColor);
      }

      .option.isDisabled {
        cursor: not-allowed;
      }

      .option.isSelected {
        outline: var(--beam-RedeemTransaction-options-selected-borderWidth) solid
          var(--beam-RedeemTransaction-options-selected-borderColor);
        background-color: var(--beam-RedeemTransaction-options-selected-backgroundColor);
        border-bottom: var(--beam-RedeemTransaction-options-selected-borderWidth) solid
          var(--beam-RedeemTransaction-options-selected-borderColor);
      }

      .option.isSelected:focus-visible,
      .option.isSelected:hover {
        outline: var(--beam-RedeemTransaction-options-selected-borderWidth) solid
          var(--beam-RedeemTransaction-options-selected-borderColor);
        border-bottom: var(--beam-RedeemTransaction-options-selected-borderWidth) solid
          var(--beam-RedeemTransaction-options-selected-borderColor);
      }

      .option .radioButton {
        border-width: 1px;
        border-style: solid;
        transition: background-color 0.2s, color 0.2s, border-color 0.2s;
        color: var(--beam-RedeemTransaction-radioButton-color);
        background-color: var(--beam-RedeemTransaction-radioButton-backgroundColor);
        border-color: var(--beam-RedeemTransaction-radioButton-borderColor);
      }

      .option.isSelected .radioButton,
      .option.isSelected:focus-visible .radioButton,
      .option.isSelected:hover .radioButton {
        color: var(--beam-RedeemTransaction-radioButton-selected-color);
        background-color: var(--beam-RedeemTransaction-radioButton-selected-backgroundColor);
        border-color: var(--beam-RedeemTransaction-radioButton-selected-borderColor);
      }

      .option:not(.isSelected):focus-visible .radioButton,
      .option:not(.isSelected):hover .radioButton {
        color: var(--beam-RedeemTransaction-radioButton-hover-color);
        background-color: var(--beam-RedeemTransaction-radioButton-hover-backgroundColor);
        border-color: var(--beam-RedeemTransaction-radioButton-hover-borderColor);
      }

      .detailsDescription {
        ${useCustomText("--beam-RedeemTransaction-options-details")}
      }

      .detailsDescription .nonprofitName {
        font-weight: var(--beam-RedeemTransaction-options-details-nonprofitName-fontWeight);
        font-style: var(--beam-RedeemTransaction-options-details-nonprofitName-fontStyle);
      }

      .radioButtonContainer {
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        flex: 0 0 auto;
      }

      .radioButton {
        padding: 5px;
        border-radius: 100%;
        display: inline-flex;
        align-items: center;
        justify-content: center;
      }

      .imageContainer {
        flex: 1 0 var(--beam-RedeemTransaction-image-width);
        display: flex;
        flex-direction: column;
      }

      .progressBarContainer {
        display: flex;
        align-items: center;
      }

      .details-progressBarLabel {
        ${useCustomText("--beam-RedeemTransaction-options-details-fundingProgressLabel")}
        white-space: nowrap;
        text-align: right;
        margin-left: 15px;
      }

      .details {
        flex: 1 1 100%;
        display: flex;
        flex-direction: column;
        gap: 5px;
      }

      .root {
        display: flex;
        flex-direction: column;
      }

      .submitButtonContainer {
        order: 4;
        position: sticky;
        bottom: 0;
        z-index: 999;
      }

      .icon-background {
        width: 50px;
        height: 50px;
        border-radius: 50%;
        overflow: hidden;
        background: var(--beam-RedeemTransaction-options-icon-backgroundColor);
        display: flex;
        justify-content: center;
        align-items: center;
      }

      .icon {
        height: var(--beam-RedeemTransaction-image-height);
      }

      .icon-container {
        display: flex;
        justify-content: center;
        align-items: center;
      }
    `,
    cssCardGrid({
      gap: "0px",
      itemMinWidth: "var(--beam-RedeemTransaction-options-minWidth, 0px)",
      columnCount: "var(--beam-RedeemTransaction-options-columnCount, 1)",
    }),
  ];

  private handleChooseClick() {
    this.didUserExpandNonprofits = true;
  }

  get shouldCollapse() {
    const enableMobileCollapse = this.cssVariables["--beam-RedeemTransaction-options-collapseNonprofits-mobile"];
    const enableDesktopCollapse = this.cssVariables["--beam-RedeemTransaction-options-collapseNonprofits-desktop"];
    const isMobileCollapseEnabled = enableMobileCollapse?.toLowerCase() === "true";
    const isDesktopCollapseEnabled = enableDesktopCollapse?.toLowerCase() === "true";

    return (this.isMobile && isMobileCollapseEnabled) || (!this.isMobile && isDesktopCollapseEnabled);
  }

  private renderSubmitButton(data: TIAPIV3GetChainNonprofits) {
    return html`
      ${this.didUserExpandNonprofits || !this.shouldCollapse
        ? html` <button
            type="button"
            ?aria-disabled=${!this.allowSubmit}
            @click="${this.handleSubmit}"
            class="${classMap({
              isDisabled: !this.allowSubmit,
              submitButton: true,
            })}"
            part="submitButton"
          >
            ${this.selectionDataController.data !== null
              ? localizeUserString(
                  this.configLang,
                  data?.config?.web?.confirmedButton || strings[this.configLang].submitButtonTextCompleted()
                )
              : localizeUserString(
                  this.configLang,
                  data?.config?.web?.confirmButton || strings[this.configLang].submitButtonText()
                )}
          </button>`
        : html`
            <button type="button" @click="${this.handleChooseClick}" class="submitButton" part="submitButton">
              ${localizeUserString(
                this.configLang,
                data?.config?.web?.collapsedButton || strings[this.configLang].chooseButtonText()
              )}
            </button>
          `}
    `;
  }

  private renderRadioButton() {
    return html`
      <div class="radioButtonContainer">
        <span part="radioButton" class="radioButton">
          <span style="line-height: 0;"> ${_radioButton({ height: "7px", width: "7px" })} </span>
        </span>
      </div>
    `;
  }

  private renderNonprofitImage(np: TIAPIV3Nonprofit, displayIcon: boolean) {
    const cardStyle = this.cssVariables["--beam-RedeemTransaction-cardStyle"];

    const imageUrl = displayIcon
      ? cardStyle === "'icon'"
        ? np.nonprofit.causeIconUrl
        : cardStyle === "'selected_icon'"
        ? np.nonprofit.causeIconSelectedUrl
        : np.imageUrl
      : np.imageUrl;

    return html`
      ${displayIcon
        ? html` <div class="icon-container">
            <div class="icon-background">
              <img src="${imageUrl}" alt="" role="presentation" class="icon" />
            </div>
          </div>`
        : html` <div
            class="image-container"
            style="${this.isMobile
              ? "flex: 1 0 var(--beam-RedeemTransaction-image-width-mobile)"
              : "flex: 1 0 var(--beam-RedeemTransaction-image-width)"}"
          >
            <img
              src="${imageUrl}"
              alt=""
              role="presentation"
              class="image"
              style="
            width: 100%;
            height: ${this.isMobile
                ? "var(--beam-RedeemTransaction-options-image-height-mobile)"
                : "var(--beam-RedeemTransaction-options-image-height)"};
            object-fit: cover;
            border-radius: var(--beam-RedeemTransaction-options-image-borderRadius);}
            "
            />
          </div>`}
    `;
  }

  private renderOptionDetail(np: TIAPIV3Nonprofit) {
    return html`
      <div class="detailsCause" style="${useCustomText("--beam-RedeemTransaction-options-details-cause")}">
        ${localizeUserString(this.configLang, np.nonprofit.cause || "")}
      </div>
      <p class="detailsDescription">${unsafeHTML(localizeUserString(this.configLang, np.impact.description || ""))}</p>
    `;
  }

  private renderProgressBar(np: TIAPIV3Nonprofit) {
    return html`
      <div class="progressBarContainer">
        <beam-progress-bar value="${np.impact.goalProgressPercentage}" style="flex: 1;"></beam-progress-bar>
        <span class="details-progressBarLabel">
          ${localizeUserString(this.configLang, np.impact.goalProgressText)}
        </span>
      </div>
    `;
  }

  private renderNonprofitDetails(
    np: TIAPIV3Nonprofit,
    hasNonprofitWithInactivePromo: boolean,
    data: TIAPIV3GetChainNonprofits
  ) {
    return html`
      <div class="details" part="details">
        ${this.renderOptionDetail(np)} ${this.renderPromoPill(np, hasNonprofitWithInactivePromo, data)}
      </div>
    `;
  }

  private renderPromoPill(
    np: TIAPIV3Nonprofit,
    hasNonprofitWithInactivePromo: boolean,
    data: TIAPIV3GetChainNonprofits
  ) {
    return html`
      ${np.promo?.isActive && this.shouldUsePromoUI && hasNonprofitWithInactivePromo
        ? html`<beam-promo-info-pill .promo=${data?.config?.web?.promo} shortVersion></beam-promo-info-pill>`
        : html``}
    `;
  }

  protected render() {
    const { selectedNonprofitId } = this;
    const { data, loading } = this.nonprofitListDataController;

    if (loading && !data) {
      return _loading();
    }
    if (this.nonprofitListDataController.error) {
      if (this.debug) {
        return _errorMessage({ error: this.nonprofitListDataController.error });
      }
      return "";
    }
    if (this.selectionDataController.error) {
      if (this.debug) {
        return _errorMessage({ error: this.selectionDataController.error });
      }
      // do not show error screen for interactive errors by default
    }
    if (data == null) {
      if (this.debug) {
        return _errorMessage({ error: new BeamError("No data") });
      }
      return "";
    }
    const nonprofits = data?.nonprofits || [];
    const selectedNonprofit = nonprofits.find((np) => np.nonprofit.id === selectedNonprofitId) || null;
    // changes images in widget to icons
    const displayIcon =
      this.cssVariables["--beam-RedeemTransaction-cardStyle"] === "'icon'" ||
      this.cssVariables["--beam-RedeemTransaction-cardStyle"] === "'selected_icon'";
    this.shouldUsePromoUI = data?.config?.web?.promo ? true : false; // new promo UI
    const hasNonprofitWithInactivePromo = nonprofits.some((nonprofit) => !nonprofit.promo || !nonprofit.promo.isActive);

    return html`
      <style>
        :host {
          ${this.cssVariables.toCSS()}
        }
      </style>
      <!-- Custom flex order fixes tab cycling so submit button is after content -->
      <div style="display: flex; flex-direction: column;" class="root">
        <div
          style="order: 3;"
          tabindex="-1"
          class="options cardGrid ${classMap({ isHidden: !this.didUserExpandNonprofits && this.shouldCollapse })}"
          part="options"
          role="radiogroup"
          aria-labelledby="beam-RedeemTransaction-title"
          ?aria-disabled=${!this.allowChange}
        >
          ${repeat(
            nonprofits,
            (i) => i.nonprofit.id,
            (np, index) => {
              const isSelected = selectedNonprofitId === np.nonprofit.id;
              const isFocusable = isSelected || (selectedNonprofit == null && index === 0);
              return html`
                <div
                  class="option ${classMap({
                    isSelected: isSelected,
                    isDisabled: !this.allowChange,
                  })}"
                  part="option"
                  role="radio"
                  tabindex="${isFocusable ? 0 : -1}"
                  data-value=${np.nonprofit.id}
                  ?aria-checked=${isSelected}
                  @click=${this.makeHandleSelect(np.nonprofit.id, index, nonprofits)}
                  @keydown=${this.makeHandleSelect(np.nonprofit.id, index, nonprofits)}
                  aria-label="${localizeUserString(this.configLang, np.nonprofit.cause || "")}"
                  style="display: flex; flex-direction: column; padding: 11px; justify-content:center;"
                >
                  <div class="optionContainer" style="display: flex; align-items: flex-start; gap: 8px;">
                    ${this.renderRadioButton()}
                    <div
                      class="nonprofitInfoContainer"
                      style="flex: 1; display: flex; flex-direction: column; gap: 5px;"
                    >
                      <div class="imageDetailContainer" style="display: flex; gap: 8px;">
                        ${this.renderNonprofitImage(np, displayIcon)}
                        ${this.renderNonprofitDetails(np, hasNonprofitWithInactivePromo, data)}
                      </div>
                      ${this.renderProgressBar(np)}
                    </div>
                  </div>
                </div>
              `;
            }
          )}
        </div>

        <div class="submitButtonContainer">${this.renderSubmitButton(data)}</div>

        <div class="headerContainer">
          <div>
            <div
              class="header-promo-pill-container"
              style="display: flex; ${this.isMobile && "flex-direction: column; align-items: flex-start;"}"
            >
              <h3
                class="title"
                part="title"
                id="beam-RedeemTransaction-title"
                style="${useCustomText("--beam-RedeemTransaction-title")}; margin-right: 8px;"
              >
                ${localizeUserString(this.configLang, data?.config?.web?.title) || strings[this.configLang].ctaTitle()}
              </h3>
              ${this.shouldUsePromoUI
                ? html`<beam-promo-info-pill
                    .promo=${data?.config?.web?.promo}
                    style="${this.isMobile && "order: -1; margin-bottom:10px;"}"
                  ></beam-promo-info-pill>`
                : html``}
            </div>
            <p class="description" part="description" style="${useCustomText("--beam-RedeemTransaction-description")}">
              ${this.shouldUsePromoUI
                ? html`<span style="font-weight:bold">
                      ${localizeUserString(this.configLang, data?.config?.web?.promoDescriptionPrefix || "") ||
                      strings[this.configLang].ctaPromoPrefixMessage()}
                    </span>
                    <span>
                      ${localizeUserString(this.configLang, data?.config?.web?.promoDescription || "") ||
                      strings[this.configLang].ctaPromoMessage()}
                    </span>`
                : html`<span
                    >${localizeUserString(this.configLang, data?.config?.web?.description || "") ||
                    strings[this.configLang].ctaMessage()}
                  </span>`}
            </p>
          </div>
        </div>

        <beam-partner-logos
          partnerName="${data.chain.name}"
          partnerLogoUrl="${data.chain.logoUrl}"
          style="order: 1"
        ></beam-partner-logos>
      </div>
    `;
  }
}

defineCustomElement(BeamRedeemTransaction);

declare global {
  interface HTMLElementTagNameMap {
    "beam-redeem-transaction": BeamRedeemTransaction;
  }
}
